/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import KeyboardShortcuts from "shortcutManager/KeyboardShortcuts";
import * as stores from "stores";
import { getSelectedTextQuads } from "utils/SelectionUtils";
import { getEventHandlerForVerb } from "shortcutManager/KeyboardShortcutUtils";
import { getFormattedShortcutExpr } from "shortcutManager/KeyboardShortcutUtils";
import { keyboardHandler, unsubscribe, subscribe } from "utils/keyboard";
import { providerUtil } from "dc-core";

export default class KeyboardShortcutsProvider {
  constructor() {
    this.shortcuts = new KeyboardShortcuts();
  }

  ready = () => Promise.resolve(this);

  /**
   * Adds a shortcut to Shortcuts Manager.
   * @param {object} shortcut Shortcut to be added
   * @returns {object} Returns an object containing success status and error if any
   */
  addShortcut = ({ id, shortcutExprs, handler }) =>
    this.shortcuts.addShortcut({
      id,
      shortcutExprs,
      handler: handler instanceof Function ? handler : getEventHandlerForVerb(handler, stores)
    });

  /**
   * Adds multiple shortcuts to Shortcuts Manager
   * @param {Array} shortcutObjs Array of shortcuts to be added
   * @returns {Array} Returns an array containing result object for each shortcut passed in params
   */
  addShortcuts = shortcutObjs => shortcutObjs.map(shortcutObj => this.addShortcut(shortcutObj));

  /**
   * Removes a shortcut from Shortcuts Manager
   * @param {string} id Id of the shortcut to be removed
   */
  removeShortcut = id => this.shortcuts.removeShortcut(id);

  /**
   * Removes multiple shortcuts from Shortcuts Manager
   * @param {Array} ids Array of ids to be removed
   */
  removeShortcuts = ids => ids.forEach(id => this.removeShortcut(id));

  /**
   * Returns shortcut corresponding to id
   * @param {string} id Id of the shortcut
   * @returns {object} Returns shortcut object
   */
  getShortcut = id => this.shortcuts.getShortcut(id);

  /**
   * Returns all shortcuts added to Shortcuts Manager
   * @returns Array of shortcut objects
   */
  getAllShortcuts = () => this.shortcuts.getAllShortcuts();

  /**
   * Registers handler function to shortcut corresponding to id
   * @param {string} id Id of the shortcut
   * @param {function} handler Function to be added as handler to the shortcut
   */
  registerShortcutHandler = (id, handler) => this.shortcuts.registerShortcutHandler(id, handler);

  /**
   * Unregisters handler function from shortcut corresponding to id
   * @param {*} id Id of the shortcut
   */
  unregisterShortcutHandler = id => this.shortcuts.unregisterShortcutHandler(id);

  /**
   * Returns display label for shortcut corresponding to id
   * @param {string} id Id of the shortcut
   * @returns
   */
  getShortcutDisplayLabel = id => this.shortcuts.getShortcutDisplayLabel(id);

  /**
   * Matches current event with registered shortcuts and triggers corresponding handler
   * @param {object} event Keydown event
   * @returns Returns an object containing boolean flags related to event handling
   */
  handleKeyDown = event => {
    const result = { handled: false, shouldPreventDefault: false };
    const handler = this.shortcuts.getHandler(event);
    const formattedShortcutExpr = getFormattedShortcutExpr(event);
    const shortcutId = this.shortcuts.idsByShortcutExpr.get(formattedShortcutExpr);

    if (handler && handler instanceof Function) {
      const selectedTextInfo = getSelectedTextQuads(stores);
      const keyboardShortcutInfo = { shortcutId, formattedShortcutExpr };
      const { shouldPreventDefault } = handler(event, { ...selectedTextInfo, keyboardShortcutInfo });
      result.handled = true;
      result.shouldPreventDefault = shouldPreventDefault;
    }

    return result;
  };

  /**
   * Removes all shortcuts from Shortcuts Manager
   */
  reset = () => {
    this.shortcuts.reset();
  };

  // Subscribe keydown event
  subscribe = () => {
    window.addEventListener("keydown", keyboardHandler);
    subscribe(this.handleKeyDown);
  };

  unsubscribe = () => unsubscribe(this.handleKeyDown);
}

KeyboardShortcutsProvider.getInstance = providerUtil.getSingletonFunction(KeyboardShortcutsProvider);
